import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Edit, Trash2, Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPartner, setEditingPartner] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/partners`);
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMaintenance = async (partnerId) => {
    try {
      await axios.patch(`${API}/partners/${partnerId}/maintenance`);
      toast({
        title: "Success",
        description: "Partner maintenance mode updated.",
      });
      fetchPartners();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update maintenance mode.",
        variant: "destructive"
      });
    }
  };

  const handleCreate = () => {
    setEditingPartner({
      name: '',
      type: '',
      logo: '',
      order: partners.length + 1
    });
    setIsCreating(true);
    setIsDialogOpen(true);
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setIsCreating(false);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (isCreating) {
        await axios.post(`${API}/partners`, editingPartner);
        toast({
          title: "Success",
          description: "Partner added successfully.",
        });
      } else {
        await axios.put(`${API}/partners/${editingPartner.id}`, editingPartner);
        toast({
          title: "Success",
          description: "Partner updated successfully.",
        });
      }
      setIsDialogOpen(false);
      fetchPartners();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save partner.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    
    try {
      await axios.delete(`${API}/partners/${partnerId}`);
      toast({
        title: "Success",
        description: "Partner deleted successfully.",
      });
      fetchPartners();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete partner.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Manage Partners">
        <div className="text-center py-8">Loading partners...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Manage Partners">
      <div className="mb-6">
        <Button onClick={handleCreate} className="bg-red-600 hover:bg-red-700">
          <Plus size={16} className="mr-2" />
          Add New Partner
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {partners.map((partner) => (
          <Card key={partner.id} className="relative">
            <CardHeader>
              {partner.maintenanceMode && (
                <Badge className="absolute top-4 right-4 bg-orange-600">
                  Check Back Soon
                </Badge>
              )}
              <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                <img 
                  src={partner.logo} 
                  alt={partner.name}
                  className="max-w-full max-h-full object-contain p-4"
                />
              </div>
              <CardTitle className="text-lg">{partner.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">{partner.type}</p>
              
              <div className="flex items-center justify-between mb-4 p-2 bg-gray-50 rounded">
                <Label htmlFor={`maintenance-${partner.id}`} className="text-sm">
                  Check Back Soon
                </Label>
                <Switch
                  id={`maintenance-${partner.id}`}
                  checked={partner.maintenanceMode || false}
                  onCheckedChange={() => toggleMaintenance(partner.id)}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(partner)}
                  className="flex-1"
                >
                  <Edit size={14} className="mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(partner.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isCreating ? 'Add New Partner' : 'Edit Partner'}</DialogTitle>
            <DialogDescription>
              {isCreating ? 'Add a new partner or sponsor.' : 'Update partner information.'}
            </DialogDescription>
          </DialogHeader>
          {editingPartner && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Partner Name *</Label>
                <Input
                  id="name"
                  value={editingPartner.name}
                  onChange={(e) => setEditingPartner({ ...editingPartner, name: e.target.value })}
                  placeholder="American Heart Association"
                />
              </div>
              <div>
                <Label htmlFor="type">Type *</Label>
                <Input
                  id="type"
                  value={editingPartner.type}
                  onChange={(e) => setEditingPartner({ ...editingPartner, type: e.target.value })}
                  placeholder="Training Network Partner"
                />
              </div>
              <div>
                <Label htmlFor="logo">Logo URL *</Label>
                <Input
                  id="logo"
                  value={editingPartner.logo}
                  onChange={(e) => setEditingPartner({ ...editingPartner, logo: e.target.value })}
                  placeholder="https://example.com/logo.png"
                />
                <p className="text-xs text-gray-500 mt-1">Enter a direct link to the logo image</p>
              </div>
              {editingPartner.logo && (
                <div>
                  <Label>Logo Preview</Label>
                  <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center mt-2">
                    <img 
                      src={editingPartner.logo} 
                      alt="Preview"
                      className="max-w-full max-h-full object-contain p-4"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/200x100/CCCCCC/666666?text=Invalid+URL';
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} className="bg-red-600 hover:bg-red-700">
                  {isCreating ? 'Add Partner' : 'Save Changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};
